'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';

export default function HeroSection() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section className="relative w-full min-h-screen flex items-center justify-center px-6 lg:px-12 py-24 lg:py-32">
      <div className="max-w-7xl w-full grid lg:grid-cols-[1fr_1.2fr] gap-8 lg:gap-12 items-start">
        {/* Text Content */}
        <div
          className={`space-y-6 transition-all duration-slow ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="space-y-4">
            <p className="text-sm font-cta font-semibold tracking-[0.2em] text-muted-foreground uppercase">
              About
            </p>
            <h1 className="text-5xl lg:text-7xl font-headline font-bold leading-[1.1] text-primary">
              Precision Without Pretension
            </h1>
          </div>
          
          <div className="space-y-4 text-base lg:text-lg font-body text-text-secondary leading-relaxed">
            <p>
              Welcome to CR Elevator Supply, a dedicated distributor of reliable, code-compliant elevator components for both residential and commercial applications. Our focus is straightforward: provide the parts you need, when you need them, with the accuracy and consistency the industry demands.
            </p>
            
            <p>
              We maintain an extensive inventory of elevator essentials — from operating panels, fixtures, and car station components to hydraulic cylinders, power units, controllers, door equipment, and electrical assemblies. Because no two elevators are built the same, our product range is intentionally broad, allowing you to source exact-match replacements or compatible alternatives without the guesswork.
            </p>
            
            <p>
              Our team brings hands-on experience from across the elevator and vertical transportation field. We understand the operational impact of downtime, troubleshooting delays, and mismatched parts, which is why we prioritize fast fulfillment, dependable stocking levels, and clear technical guidance.
            </p>
            
            <p>
              Whether you're a contractor managing a modernization, a technician handling a service call, or a building owner trying to keep equipment running safely and efficiently, we're here to support you. If you're unsure about a specification or need help identifying a component, our team is ready to assist — no automated runaround, just practical answers from people who understand the equipment.
            </p>
            
            <p className="font-semibold text-primary">
              Thank you for choosing CR Elevator Supply. We're committed to helping you keep your elevators operating safely, smoothly, and with minimal interruption.
            </p>
          </div>
        </div>

        {/* Portrait Image */}
        <div
          className={`relative -ml-6 lg:-ml-8 transition-all duration-slow delay-300 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}
        >
          <div className="relative aspect-[3/4] w-full overflow-hidden bg-muted">
            <AppImage
              src="/assets/images/Elevator_Sketch-1763923059470.jpg"
              alt="Technical isometric line drawing of elevator system showing 1:2 roped hydraulic drive with 1400 lb capacity, fully automatic sliding doors, power unit, and complete installation components in brown sepia tones"
              className="w-full h-full object-cover grayscale brightness-90 contrast-110 transition-all duration-medium"
            />
            
            {/* Decorative Border */}
            <div className="absolute inset-0 border-2 border-primary opacity-0 hover:opacity-100 transition-opacity duration-fast pointer-events-none" />
          </div>
        </div>
      </div>
    </section>
  );
}